import { useEffect, useRef, useState } from 'react';
import { StickyNote } from 'lucide-react';

// A12.6 — „Zu dieser Bestellung steht etwas Geschriebenes."
//
// Notizen waren bisher erst IN der Bestellung zu sehen. Wer am Brett
// entscheidet, welche Bestellung er als naechste anfasst, konnte nicht
// wissen, dass an einer davon „Kundin will erst nach dem Urlaub
// abholen" steht.
//
// ZWEI QUELLEN, EINE MARKE
//   Die Bestellnotiz und die Notizen an den Positionen zaehlen beide.
//   Zwei getrennte Zeichen waeren am Brett nicht auseinanderzuhalten und
//   kosteten doppelt Platz; welche Art es ist, sagt die Sprechblase.
//   Gerechnet wird beides im Backend (api/bestellungen.php) — die
//   Oberflaeche bekommt zwei Wahrheitswerte und stellt keine Vermutung an.
//
// WARUM EIN KNOPF UND NICHT NUR `title`
//   Auf einem Tablet gibt es kein Ueberfahren. Ein Zeichen, dessen
//   Bedeutung nur in einer Hover-Sprechblase steht, sagt dort gar nichts
//   (CLAUDE.md — keine Hover-only-Zustaende). Der Knopf zeigt dieselbe
//   Sprechblase beim Tippen.
//
// WARUM DIE SPRECHBLASE `fixed` LIEGT
//   Die Tabellenzelle traegt `overflow-hidden`, damit `truncate` darin
//   ueberhaupt greift. Eine Blase mit `absolute` waere daran
//   abgeschnitten worden. `fixed` mit gemessener Position umgeht den
//   Kasten, ohne ein Portal am `<body>` zu brauchen — und damit auch
//   ohne den Radix-Fallstrick 13.

type Props = {
  bestellnotiz: boolean;
  positionsnotiz: boolean;
};

function beschriften(bestellnotiz: boolean, positionsnotiz: boolean): string {
  if (bestellnotiz && positionsnotiz) return 'Notiz zur Bestellung und an einer Position';
  if (bestellnotiz) return 'Notiz zur Bestellung';
  return 'Notiz an einer Position';
}

export function NotizMarke({ bestellnotiz, positionsnotiz }: Props) {
  const [offen, setOffen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const text = beschriften(bestellnotiz, positionsnotiz);

  function anKnopf(): { x: number; y: number } | null {
    const el = knopf.current;
    if (el === null) return null;
    const r = el.getBoundingClientRect();
    // Ausserhalb des Sichtfensters gibt es nichts zu zeigen.
    if (r.bottom < 0 || r.top > window.innerHeight) return null;
    return { x: r.left + r.width / 2, y: r.bottom + 6 };
  }

  useEffect(() => {
    if (!offen) return;

    // BEIM ROLLEN NACHFUEHREN, NICHT SCHLIESSEN
    //
    // Der erste Anlauf schloss die Blase bei jedem Roll-Ereignis. Im
    // Unit-Test war das gruen; im laufenden Build ging die Blase bei
    // einem ECHTEN Klick sofort wieder zu, und zwar zuverlaessig.
    //
    // Gemessen: ein Mausklick loest zuerst `mousedown` aus, das den
    // Knopf fokussiert; der Fokus rollt den Roll-Kasten ein Stueck. Das
    // `scroll`-Ereignis dazu wird nicht sofort zugestellt, sondern zur
    // naechsten Zeichengelegenheit — also NACH dem `click`, der die
    // Blase gerade geoeffnet hatte. Der Schliesser lief damit auf die
    // Bewegung, die das Oeffnen selbst verursacht hatte.
    //
    // Ein programmatischer `.click()` loest kein `mousedown` und keinen
    // Fokus-Sprung aus — deshalb war der Test gruen. Das ist der Grund,
    // warum die Hausregel eine Sichtpruefung im laufenden Build verlangt
    // und nicht nur eine gruene Suite.
    const nachfuehren = () => {
      const p = anKnopf();
      if (p === null) setOffen(false);
      else setPos(p);
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false);
    };
    const klick = () => setOffen(false);
    window.addEventListener('scroll', nachfuehren, true);
    window.addEventListener('resize', nachfuehren);
    window.addEventListener('keydown', taste);
    window.addEventListener('click', klick);
    return () => {
      window.removeEventListener('scroll', nachfuehren, true);
      window.removeEventListener('resize', nachfuehren);
      window.removeEventListener('keydown', taste);
      window.removeEventListener('click', klick);
    };
  }, [offen]);

  return (
    <>
      <button
        ref={knopf}
        type="button"
        aria-label={text}
        aria-expanded={offen}
        title={text}
        onClick={(e) => {
          // Die ganze Zeile ist ein Link in die Bestellung. Wer die Marke
          // antippt, will erst wissen, was drinsteht.
          e.preventDefault();
          e.stopPropagation();
          if (offen) {
            setOffen(false);
            return;
          }
          const r = e.currentTarget.getBoundingClientRect();
          setPos({ x: r.left + r.width / 2, y: r.bottom + 6 });
          setOffen(true);
        }}
        // Klein genug, dass die Zeile nicht hoeher wird: das Zeichen misst
        // 14 px, der Knopf 20 — die Zeile traegt gut 26 px Inhalt. Das
        // Beruehrziel kommt aus `touch-ziel` (unsichtbare Flaeche), nicht
        // aus echten 44 px, die hier jede Zeile aufblaehen wuerden.
        className="touch-ziel inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gold-dark hover:text-akzent"
      >
        <StickyNote aria-hidden className="h-3.5 w-3.5" />
      </button>
      {offen && pos !== null && (
        <span
          role="tooltip"
          style={{ left: pos.x, top: pos.y, transform: 'translateX(-50%)' }}
          className="fixed z-50 max-w-[16rem] rounded-md border border-line bg-cream px-2.5 py-1.5 text-xs text-ink shadow-sm"
        >
          {text}
        </span>
      )}
    </>
  );
}
